import type { RequestTiming } from '../core/api/client.js';

/**
 * Telemetry for the client portal, into the SAME Application Insights component as the API and the
 * mobile app.
 *
 * **Why the portal gets this at all, given nobody asked for it.** The portal is where an agency's
 * *end clients* look at their own calls — people who never chose Fig and whose opinion becomes the
 * agency's opinion of us. If it is slow for them, the agency hears about it and we do not. That is
 * the exact failure Rasel named for the app: *"we'll be left wondering, because we'll just assume
 * that it's just bad and no one wants it."* It applies here with more force, because there is one
 * more person between us and the complaint.
 *
 * **Deliberately the same shape as `fig-mobile/src/lib/telemetry.ts`, not a shared module.** Two
 * reasons. The envelope tags differ (`ai.cloud.role`, no `Platform`), and more importantly the
 * browser has `@microsoft/applicationinsights-web` available while React Native does not — so a
 * shared implementation would mean the portal permanently using the worse of the two options to
 * keep a file in common. Keeping the PATTERNS identical and the implementations separate is the
 * trade that costs least: the scrubbing patterns below are copied verbatim and must stay that way,
 * because a value redacted on one client and not the other looks safe and is not.
 */

interface Config {
  instrumentationKey: string;
  ingestionEndpoint: string;
}

function parseConnectionString(raw: string | undefined): Config | null {
  if (!raw) return null;
  const parts = Object.fromEntries(
    raw.split(';').map((p) => {
      const i = p.indexOf('=');
      return i < 0 ? ['', ''] : [p.slice(0, i).trim().toLowerCase(), p.slice(i + 1).trim()];
    }),
  );
  const key = parts.instrumentationkey;
  if (!key) return null;
  const endpoint = parts.ingestionendpoint?.replace(/\/+$/, '')
    ?? 'https://dc.services.visualstudio.com';
  return { instrumentationKey: key, ingestionEndpoint: endpoint };
}

/**
 * The App Insights INGESTION connection string, as a default in source (2026-10-21).
 *
 * Not a secret: it can only send telemetry, never read any back, which is why every telemetry SDK
 * ships it inside the client, and why the mobile app has carried this same value in eas.json since
 * build 41. It lives here rather than in the deploy workflows because those files are protected
 * from automated edits, and Rasel asked that wiring telemetry not need a manual step. A build that
 * sets VITE_APPINSIGHTS_CONNECTION_STRING still wins, so pointing a build elsewhere stays possible.
 */
const DEFAULT_CONNECTION_STRING =
  'InstrumentationKey=8c4acd05-2fc9-4c88-b8d8-c15863fe8698;'
  + 'IngestionEndpoint=https://australiaeast-1.in.applicationinsights.azure.com/;'
  + 'LiveEndpoint=https://australiaeast.livediagnostics.monitor.azure.com/;'
  + 'ApplicationId=22503664-9fa5-4c6d-a12a-ab962680a733';

const config = parseConnectionString(
  (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined)
    // Not in local development (`vite` dev server): a laptop's clicks are not usage.
    ?? (import.meta.env.DEV ? undefined : DEFAULT_CONNECTION_STRING),
);

/**
 * Identical to the server's and the app's patterns, on purpose. Fig stores callers' phone numbers
 * beside transcripts of what they said; a value scrubbed in one place and not another is worse
 * than scrubbing neither, because it looks safe.
 */
const REDACTIONS: [RegExp, string][] = [
  [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[jwt]'],
  [/\b(sk|pk|xi)[-_][A-Za-z0-9]{16,}\b/gi, '[key]'],
  [/\+\d{7,15}\b/g, '[phone]'],
  [/\b0[2-478]\d{8}\b/g, '[phone]'],
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[email]'],
];

export function scrub(value: string): string {
  return REDACTIONS.reduce((acc, [re, to]) => acc.replace(re, to), value);
}

let context: Record<string, string> = {};

/** Set once the session is known, so a failure can be attributed without carrying an identity. */
export function setTelemetryContext(next: Record<string, string | undefined>) {
  context = { ...context };
  for (const [k, v] of Object.entries(next)) {
    if (v) context[k] = scrub(v);
    else delete context[k];
  }
}

function envelope(name: string, baseType: string, baseData: Record<string, unknown>) {
  return {
    name: `Microsoft.ApplicationInsights.${name}`,
    time: new Date().toISOString(),
    iKey: config?.instrumentationKey,
    tags: {
      // The role is what separates the portal's rows from the app's and the API's in one
      // component. Splitting components instead is how you end up looking at the wrong one.
      'ai.cloud.role': 'fig-portal',
      'ai.cloud.roleInstance': 'browser',
      'ai.application.ver': (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev',
      ...(context.sessionId ? { 'ai.session.id': context.sessionId } : {}),
    },
    data: { baseType, baseData: { ver: 2, ...baseData } },
  };
}

/**
 * Fire-and-forget, and `keepalive` so a report started during a navigation is not cancelled by it
 * — which is exactly when the slowest requests happen and therefore exactly the data that would go
 * missing.
 */
function send(payload: unknown) {
  if (!config) return;
  try {
    void fetch(`${config.ingestionEndpoint}/v2/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* never let telemetry break the page */
  }
}

/** App Insights wants `D.HH:MM:SS.fff`. A number lands as a null duration, which reads as unmeasured. */
function isoDuration(ms: number): string {
  const c = Math.max(0, Math.round(ms));
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `0.${pad(Math.floor(c / 3_600_000))}:${pad(Math.floor((c % 3_600_000) / 60_000))}`
    + `:${pad(Math.floor((c % 60_000) / 1000))}.${pad(c % 1000, 3)}`;
}

/**
 * One API call, as the BROWSER experienced it. Sent as `RemoteDependencyData` so Application
 * Insights' own latency, failure-rate and p95-by-name views work without a dashboard being built
 * first. `info.route` is already a template with the query string stripped — see `routeTemplate` in
 * the core. Nothing here may widen that.
 */
export function trackRequest(info: RequestTiming) {
  send(envelope('RemoteDependency', 'RemoteDependencyData', {
    name: `${info.method} ${info.route}`,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    resultCode: String(info.status),
    duration: isoDuration(info.durationMs),
    success: info.ok,
    type: 'HTTP',
    target: apiHost,
    properties: {
      ...context,
      cold: String(info.cold),
      attempt: String(info.attempt),
      reached: String(info.status !== 0),
    },
  }));
}

/** The API's host only. Never a full URL — a path can carry an id. */
const apiHost = (() => {
  try { return new URL((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://unknown').host; }
  catch { return 'unknown'; }
})();

export function trackEvent(name: string, properties: Record<string, string | number | boolean> = {}) {
  const props: Record<string, string> = { ...context };
  for (const [k, v] of Object.entries(properties)) props[k] = scrub(String(v));
  send(envelope('Event', 'EventData', { name, properties: props }));
}

export function trackError(error: unknown, extra: Record<string, string> = {}) {
  const e = error instanceof Error ? error : new Error(String(error));
  const props: Record<string, string> = { ...context };
  for (const [k, v] of Object.entries(extra)) props[k] = scrub(v);
  send(envelope('Exception', 'ExceptionData', {
    severityLevel: 3,
    exceptions: [{
      typeName: e.name,
      message: scrub(e.message || 'Unknown error'),
      hasFullStack: !!e.stack,
      parsedStack: (e.stack ?? '').split('\n').slice(0, 30)
        .map((line, i) => ({ level: i, method: scrub(line.trim()).slice(0, 300), assembly: 'fig-portal' })),
    }],
    properties: props,
  }));
}

/** True when telemetry is actually configured, so a startup log can say so honestly. */
export const telemetryEnabled = config !== null;
